import { useContext, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import isString from 'lodash/isString'

import { ETimeUnit, usePrompt } from '@hooks'
import { mapValueTypeToValueFormat } from '@helpers'
import { BIND_TYPE, FIELD_VALUE_FORMAT, FIELD_VALUE_TYPE, SEARCH_FIELD_TYPE } from '@constants'
import { AutocompleteOption, POSTCriteriaValueType } from '@types'

import { SearchAssistantsCreateOrEditContext } from '../../../SearchAssistantsCreateOrEdit'
import { EmbeddedObjectShort, SearchFilterValue } from '../../../types'
import { SearchFilterForm } from '../AddSearchFilterDialog'
import { getDefaultValues } from '../helpers'

import { useInputs } from './useInputs'

export const useAddSearchFilterDialog = () => {
  const defaultValues = getDefaultValues()
  const methods = useForm<SearchFilterForm>({ defaultValues })
  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods

  usePrompt({ when: isDirty })
  const {
    closeAddSearchFilterModal,
    objects,
    createOrUpdateCriteria,
    editCriteria,
    setCriteriaTable,
    searchAssistantId,
  } = useContext(SearchAssistantsCreateOrEditContext)

  useEffect(() => {
    if (editCriteria) {
      reset({
        id: editCriteria.id,
        title: editCriteria.title,
        objectTitle: editCriteria.objectTitle,
        code: editCriteria.code,
        isPinning: editCriteria.isPinning || false,
        visibility: editCriteria.visibility,
        object: {
          label: editCriteria.objectTitle,
          id: editCriteria.objectCode,
        },
        values: editCriteria.values.map(value => {
          return {
            id: value.id,
            name: value.name,
            fieldType: value.fieldType ? { id: value.fieldType, label: value.fieldType } : null,
            formatDate: value.formatDate ? { id: value.formatDate, label: value.formatDate } : null,
            objectValue: value.objectValue ? value.objectValue : null,
            linkedObjectCode: value.linkedObjectCode || '',
            isDefaultPlaceholder: Boolean(value.isDefaultPlaceholder),
            isGroupRelated: Boolean(value.isGroupRelated),
            placeholderValue: value.placeholderValue || '',
            placeholderStart: value.placeholderStart || '',
            placeholderEnd: value.placeholderEnd || '',
            timeIncrement: value.timeIncrement || null,
            dropDownListCode: value.dropDownList
              ? { id: value.dropDownList.code, label: value.dropDownList.title }
              : null,
            dropDownWindowCode: value.dropDownWindow
              ? { id: value.dropDownWindow.code, label: value.dropDownWindow.title }
              : null,
            dropDownListFilters: value.dropDownListFilters || [],
            dropDownWindowFilters: value.dropDownWindowFilters || [],
            valueType: value.valueType,
            asCheckbox:
              value.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
              value.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN,
            asDuration: value.valueFormat === FIELD_VALUE_FORMAT.TIME,
            pathArray: value.pathArray || [],
            valuePathArray: value.valuePathArray,
            min: value.min,
            max: value.max,
            timeUnit: value.timeUnit
              ? { id: value.timeUnit, label: value.timeUnit }
              : { id: ETimeUnit.HOUR, label: ETimeUnit.HOUR },
            isFilterByObject: value.isFilterByObject,
          }
        }),
      })
    }
  }, [editCriteria])

  const objectOptions = useMemo((): AutocompleteOption[] => {
    if (!objects?.length) {
      return []
    }

    return objects.map(object => ({ id: object.code, label: object.title }))
  }, [objects])

  const prepareValues = (
    rawValues: SearchFilterValue[],
    objectCode: string
  ): POSTCriteriaValueType[] => {
    return rawValues
      .filter(value => Boolean(value.name))
      .map(value => {
        const isLinkedField = value.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK
        // Берется value.name.id, т.к. в name хранится объект филда, где id это его objectCode (ref или не ref)
        // Если поле не реф, то прокидывается рутовый (критерии) objectCode
        const linkedObjectCodeWhenCreate =
          isLinkedField && isString(value.name?.id) ? value.name.id : objectCode

        const transformPathArray: EmbeddedObjectShort[] = value.pathArray
          ? value.pathArray.map(obj => {
              return { field: obj.field, objectCode: obj.objectCode, valueType: obj.valueType }
            })
          : []

        const transformedValue = {
          id: value.id,
          name: value.name,
          objectValue: value.objectValue ? value.objectValue : '',
          pathArray: transformPathArray,
          fieldType: value.fieldType ? (value.fieldType.label as SEARCH_FIELD_TYPE) : undefined,
          formatDate: value.formatDate ? value.formatDate.label : undefined,
          linkedObjectCode: value.linkedObjectCode
            ? value.linkedObjectCode
            : linkedObjectCodeWhenCreate,
          valuePathArray: value.valuePathArray,
          dropDownListCode: value.dropDownListCode ? String(value.dropDownListCode.id) : undefined,
          dropDownWindowCode: value.dropDownWindowCode
            ? String(value.dropDownWindowCode.id)
            : undefined,
          placeholderValue: value.placeholderValue || '',
          timeIncrement: value.timeIncrement || null,
          dropDownListFilters: value.dropDownListFilters || [],
          dropDownWindowFilters: value.dropDownWindowFilters || [],
          placeholderStart: value.placeholderStart || '',
          placeholderEnd: value.placeholderEnd || '',
          isDefaultPlaceholder: Boolean(value.isDefaultPlaceholder),
          isGroupRelated: value.isGroupRelated,
          asCheckbox: value.asCheckbox,
          asDuration: value.asDuration,
          valueType: value.valueType,
          min: value.min,
          max: value.max,
          timeUnit: value.timeUnit?.id || ETimeUnit.HOUR,
          isFilterByObject: value.isFilterByObject,
        }

        const isLinkedObjectValue =
          transformedValue.objectValue && transformedValue.linkedObjectCode

        const isDateValue = Boolean(transformedValue.fieldType)

        const {
          name,
          placeholderValue,
          isDefaultPlaceholder,
          placeholderEnd,
          placeholderStart,
          isGroupRelated,
          asCheckbox,
          asDuration,
          valueType,
          valuePathArray,
          isFilterByObject,
        } = transformedValue

        const valueFormat = {
          valueFormat: transformedValue
            ? mapValueTypeToValueFormat(valueType, asCheckbox, asDuration)
            : null,
        }

        // На данный момент могут быть такие name, как
        // bound (ENUM)
        // или
        // _id (PK)
        // Для корректности данных, текст в скобках отправлять не нужно
        const nameForValue = name?.split(' ')[0]

        return {
          name: nameForValue,
          placeholderValue,
          isDefaultPlaceholder,
          placeholderStart,
          placeholderEnd,
          valuePathArray,
          id: transformedValue.id,
          dropDownListFilters:
            isLinkedObjectValue || isFilterByObject
              ? transformedValue.dropDownListFilters
              : undefined,
          dropDownWindowFilters:
            isLinkedObjectValue || isFilterByObject
              ? transformedValue.dropDownWindowFilters
              : undefined,
          dropDownListCode:
            isLinkedObjectValue || isFilterByObject ? transformedValue.dropDownListCode : undefined,
          dropDownWindowCode:
            isLinkedObjectValue || isFilterByObject
              ? transformedValue.dropDownWindowCode
              : undefined,
          objectValue: isLinkedObjectValue ? transformedValue.objectValue : undefined,
          pathArray: isLinkedObjectValue ? transformedValue.pathArray : undefined,
          linkedObjectCode: transformedValue.linkedObjectCode,
          fieldType: isDateValue ? transformedValue.fieldType : undefined,
          formatDate: transformedValue?.formatDate,
          bindType: BIND_TYPE.FIELD,
          timeIncrement: transformedValue.timeIncrement
            ? transformedValue.timeIncrement
            : undefined,
          min: transformedValue.min,
          max: transformedValue.max,
          timeUnit: transformedValue.timeUnit,
          isGroupRelated,
          isFilterByObject,
          ...valueFormat,
        }
      })
  }

  const handleSave = handleSubmit((data: SearchFilterForm) => {
    if (data.object?.id && searchAssistantId) {
      createOrUpdateCriteria({
        id: data.id,
        searchAssistantId,
        code: data.code,
        objectCode: data.object?.id as string,
        objectTitle: data.object?.label as string,
        title: data.title,
        isPinning: data.isPinning,
        values: prepareValues(data.values, data.object.id as string),
        visibility: data.visibility,
      })
        .unwrap()
        .then(result => {
          if (data.id) {
            setCriteriaTable(prev =>
              prev.map(template => (template.id === result.id ? result : template))
            )
            closeAddSearchFilterModal()
            reset()

            return
          }
          setCriteriaTable(prev => {
            const newData = [...prev]
            newData.unshift(result)

            return newData
          })
          closeAddSearchFilterModal()
          reset()
        })
    }
  })

  const searchFilterInputs = useInputs({
    objectOptions,
  })

  return {
    isDirty,
    handleSave,
    methods,
    searchFilterInputs,
  }
}
